import { reactive } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import i18n from '@/js/plugins/i18n';
import { outerHeight } from '@/components/Reusables/utils';
import clientConfig from '@/client_customs/config';
import PlanningLane from '@/models/PlanningLane';
import useUndoRedoStore from '@/stores/undoredo';
import useSubprojectsStore from '@/stores/subprojects';
import usePlanningStore from './planning';
import usePlanningElementsStore from './elements';
import useSelectionStore from '@/stores/selection';

let undoState = [];

function insertNewLane(planning, newlane) {
  const lane = reactive(new PlanningLane(planning, newlane));
  planning.lanes.push(lane);
  return lane;
}

function set(planning, newlanes) {
  planning.lanes.splice(0, planning.lanes.length);
  while (newlanes.length) {
    const newlane = newlanes.shift();
    insertNewLane(planning, newlane);
  }
}

let idCounter = 1;
function nextId(lanes) {
  idCounter = Math.max(idCounter, Math.max(...lanes.map(item => item.id), 0)) + 1;
  return idCounter;
}

function undoredoAndSave() {
  const planningStore = usePlanningStore();
  const { planning } = planningStore;
  if (angular.equals(planning.lanes, undoState)) return;
  const newState = planning.lanes.map(lane => angular.copy(lane));
  const oldState = undoState.map(lane => angular.copy(lane));

  useUndoRedoStore().add({
    action: () => {
      set(planning, newState.map(lane => angular.copy(lane)));
    },
    rollback: () => {
      set(planning, oldState.map(lane => angular.copy(lane)));
    },
  });
  planningStore.save();
}

const store = defineStore('planning-lanes', {
  getters: {
    lanesMap() {
      const { planning } = usePlanningStore();
      const map = new Map();
      planning.lanes.forEach((lane) => {
        map.set(lane.id, lane);
      });
      return map;
    },
    getVisibleLanes() {
      const { planning: rootPlanning } = usePlanningStore();
      if (clientConfig.lanes?.hideEmptySublanes) {
        return function ({ planning = rootPlanning } = {}) {
          if (planning == rootPlanning) return rootPlanning.lanes.filter(lane => lane.isVisible());
          // filter empty sublanes
          return planning.lanes.filter(sublane => sublane.isVisible() && planning.elements.some(el => el.getLaneId() == sublane.id && el.isVisibleOnPlanning()));
        };
      }
      return function ({ planning = rootPlanning } = {}) {
        return planning.lanes.filter(lane => lane.isVisible());
      };
    },
    getLaneElements() {
      const { planning: rootPlanning } = usePlanningStore();
      const elementsByLaneId = new Map();
      rootPlanning.elements?.forEach((el) => {
        if (! elementsByLaneId.has(el.data.lane_id)) elementsByLaneId.set(el.data.lane_id, []);
        elementsByLaneId.get(el.data.lane_id).push(el);
      });

      return function ({ planning = rootPlanning, laneId }) {
        if (planning == rootPlanning) {
          return elementsByLaneId.get(laneId) || [];
        }
        return planning.elements.filter(el => el.data.lane_id == laneId);
      };
    },
    getLaneHeaderHeight() {
      const lanesMapGetter = this.lanesMap;
      return function (laneId) {
        const lane = lanesMapGetter.get(laneId);
        if (lane && lane.hidden) return 0;
        const domEl = document.getElementById(`laneheader${laneId}`);
        if (! domEl) return 0;
        const originalHeight = domEl.style.height;
        const originalMinHeight = domEl.style['min-height'];
        domEl.style.height = "auto";
        domEl.style['min-height'] = "auto";
        const laneHeaderHeight = outerHeight(domEl);
        domEl.style.height = originalHeight;
        domEl.style['min-height'] = originalMinHeight;
        return laneHeaderHeight;
      };
    },
    getLaneHeadersHeights() {
      const { planning } = usePlanningStore();
      const laneHeadersHeights = {};
      planning.lanes.forEach((lane) => {
        // eslint-disable-next-line no-unused-vars
        const { label, contentHeight } = lane;
        laneHeadersHeights[lane.id] = outerHeight(document.getElementById(`laneheader${lane.id}`));
      });
      return laneHeadersHeights;
    },
  },

  actions: {
    addLane(newlane) {
      const planningStore = usePlanningStore();
      const { planning } = planningStore;
      undoState = planning.lanes.map(lane => angular.copy(lane));
      const lane = insertNewLane(planning, newlane);
      lane.id = nextId(planning.lanes);
      lane.label = newlane?.label || i18n.global.t('PLANNING.NEW_LANE') || '';
      undoredoAndSave();
      return lane;
    },
    deleteLaneElements(deletedLanes) {
      deletedLanes.forEach((lane) => {
        const laneElements = this.getLaneElements({ laneId: lane.id });
        laneElements.forEach((el) => {
          usePlanningElementsStore().remove(el);
          useSelectionStore().selection.delete(el.id);
        });
      });
    },
    deleteLane(laneId) {
      const planningStore = usePlanningStore();
      const { planning } = planningStore;
      useUndoRedoStore().transaction(() => {
        const index = planning.lanes.findIndex(lane => lane.id == laneId);
        const lane = planning.lanes[index];
        if (lane?.project_id) {
          useSubprojectsStore().removeByLaneId(lane.id);
        }
        if (lane) {
          planning.lanes.splice(index, 1);
        }
        this.deleteLaneElements([lane]);
      });

      planningStore.save();
    },
    updateAllLanes({ lanesToAdd = [], lanesToDelete = [], lanesToUpdate = [], lanesIdsOrder = null }) {
      const planningStore = usePlanningStore();
      const { planning } = planningStore;
      useUndoRedoStore().transaction(() => {
        // add
        lanesToAdd.forEach((newlane) => {
          const lane = new PlanningLane(planning, newlane);
          lane.id = nextId(planning.lanes);
          lane.label = newlane?.label || i18n.global.t('PLANNING.NEW_LANE') || '';
          if (lanesIdsOrder) {
            // needed because new lanes receive their real id here (not in lanesIdsOrder).
            planning.lanes.splice(lanesIdsOrder.indexOf(newlane.id), 0, lane);
          } else {
            planning.lanes.push(lane);
          }
        });

        // delete
        const realDeletedLanes = [];
        lanesToDelete.forEach((laneToDelete) => {
          const index = planning.lanes.findIndex(item => item.id == laneToDelete.id);
          if (index == -1) return;
          const lane = planning.lanes[index];
          if (lane?.project_id) {
            useSubprojectsStore().removeByLaneId(lane.id);
          }
          if (lane) {
            planning.lanes.splice(index, 1);
          }
          realDeletedLanes.push(lane);
        });

        this.deleteLaneElements(realDeletedLanes);
        // update
        for (let i = 0; i < lanesToUpdate.length; i++) {
          const lane = this.lanesMap.get(lanesToUpdate[i].id);
          if (lane.hidden != lanesToUpdate[i].hidden) this.setLaneHidden({ lane, hidden: lanesToUpdate[i].hidden });
          lane.set(lanesToUpdate[i].getAll());
        }

        // sort
        if (lanesIdsOrder) {
          planning.lanes.sort((a, b) => {
            const indexA = lanesIdsOrder.indexOf(a.id);
            const indexB = lanesIdsOrder.indexOf(b.id);
            if (indexA == -1 || indexB == -1) {
              return 0;
            }
            return indexA < indexB ? -1 : 1;
          });
        }
      });

      planningStore.save();
    },
    startSortLanes() {
      const { planning } = usePlanningStore();
      undoState = planning.lanes.map(lane => angular.copy(lane));
    },
    updateSortLanes() {
      undoredoAndSave();
    },
    startChangingContent() {
      const { planning } = usePlanningStore();
      undoState = planning.lanes.map(lane => angular.copy(lane));
    },
    changingContent() {
      const { planning } = usePlanningStore();
      if (! angular.equals(planning.lanes, undoState)) {
        undoredoAndSave();
      }
    },
    setLaneHidden({ lane, hidden = false }) {
      lane.hidden = hidden;
      if (lane.hidden) return;
      const laneElements = this.getLaneElements({ laneId: lane.id }) || [];
      setTimeout(() => {
        laneElements.forEach((el) => {
          el.update();
          el.updateHeight();
        });
      });
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
